import { useEffect, useMemo, useState } from "react";
import { Plus, Building2, Layers } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { ProjectCard } from "@/components/ProjectCard";
import { CreateProjectDialog } from "@/components/CreateProjectDialog";
import { BulkUploadDialog } from "@/components/BulkUploadDialog";
import { FxRatesCard } from "@/components/FxRatesCard";
import { BpsMarketCard } from "@/components/BpsMarketCard";
import { Button } from "@/components/ui/button";
import { api, errorText } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function DashboardPage() {
  const [projects, setProjects] = useState(null);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [bulk, setBulk] = useState(false);
  const navigate = useNavigate();
  const { can } = useAuth();

  const [registered, setRegistered] = useState([]);
  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch((e) => toast.error(errorText(e)));
  useEffect(() => { load(); api.get("/properties").then((r) => setRegistered(r.data.filter((p) => p.is_active).map((p) => p.name))).catch(() => setRegistered([])); }, []);

  const properties = useMemo(() => [...registered].sort(), [registered]);
  const visible = (projects || []).filter((p) => registered.includes(p.hotel_name) && (filter === "all" || p.hotel_name === filter));

  const remove = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"?`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Project deleted");
      load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <AppNav />
      <main className="px-4 sm:px-8 lg:px-12 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 rise">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-emerald-700 font-semibold">Property Portfolio</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-2">Budget Workspace</h1>
            <p className="text-slate-600 mt-3 max-w-2xl text-sm sm:text-base">Pick a property, upload the actual P&amp;L reports, then generate next year's USALI budget complete with forecast and macro assumptions.</p>
          </div>
          {can("upload") && <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setBulk(true)} data-testid="bulk-upload-button" className="h-11 px-5 border-slate-300">
              <Layers size={16} className="mr-2" /> Bulk Property Upload
            </Button>
            <Button onClick={() => setOpen(true)} data-testid="create-project-button" className="h-11 bg-slate-900 hover:bg-emerald-700 text-white px-5">
              <Plus size={16} className="mr-2" /> New Budget
            </Button>
          </div>}
        </div>

        <FxRatesCard />
        <BpsMarketCard />

        <div className="mt-10 flex flex-wrap gap-2 rise rise-1" data-testid="property-filter">
          <button onClick={() => setFilter("all")} className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${filter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`} data-testid="property-filter-all">All properties</button>
          {properties.map((p) => (
            <button key={p} onClick={() => setFilter(p)} className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${filter === p ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-200 hover:border-emerald-400"}`} data-testid={`property-filter-${p.replace(/\s+/g, "-").toLowerCase()}`}>{p}</button>
          ))}
        </div>

        {projects === null ? (
          <p className="mt-10 text-slate-500" data-testid="projects-loading">Loading projects...</p>
        ) : visible.length === 0 ? (
          <div className="mt-12 border border-dashed border-slate-300 rounded-lg p-14 text-center bg-white rise rise-2" data-testid="projects-empty">
            <Building2 className="mx-auto text-emerald-600" size={32} />
            <h3 className="font-heading text-xl font-semibold mt-4 text-slate-900">No budget projects yet</h3>
            <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">Start by creating a project for one of the Armella Group properties, then upload the actual P&amp;L files.</p>
            {can("upload") && <Button onClick={() => setOpen(true)} className="mt-6 bg-emerald-700 hover:bg-emerald-800" data-testid="create-project-empty-button"><Plus size={16} className="mr-2" /> New Budget</Button>}
          </div>
        ) : (
          <div className="mt-8 grid gap-4 sm:gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="projects-grid">
            {visible.map((p, i) => <ProjectCard key={p.id} project={p} onDelete={can("edit") ? remove : null} index={i} />)}
          </div>
        )}
      </main>
      <CreateProjectDialog open={open} onOpenChange={setOpen} onCreated={(p) => navigate(`/projects/${p.id}`)} />
      <BulkUploadDialog open={bulk} onOpenChange={setBulk} onDone={load} />
    </div>
  );
}
